import { isFormula, type WorkbookCell } from "./workbook";
import { columnNumberToName, escapeXml } from "./xml";

/**
 * A pivot table over a table on another sheet.
 *
 * Excel reads the rows itself when the file is opened (`refreshOnLoad`), so no copy of the data
 * is stored. What is written is the layout: which fields sit in the rows and which are summed,
 * and for those fields the items they show, because Excel refuses a placed field without them.
 */
export interface PivotTableSpec {
  /** 0-based index of the sheet holding the data. */
  sourceSheet: number;
  /** 1-based row of the source's header; the field names are read from it. */
  headerRow: number;
  /** 1-based last row of the data. */
  lastRow: number;
  /** One per column of the source, in order. */
  fieldNames: string[];
  /** The data rows below the header, as on the source sheet. Read for the placed fields only. */
  rows?: WorkbookCell[][];
  /** Without one, every field is offered and none placed. */
  layout?: PivotLayout;
  /** 1-based row the pivot table starts in, below anything else on its sheet. Row 3 if left out. */
  locationRow?: number;
}

export interface PivotLayout {
  /** Outermost first. */
  rowFields: PivotRowField[];
  dataFields: PivotDataField[];
  /** Each row field in a column of its own with its name above it, instead of one indented column. */
  tabular?: boolean;
}

/**
 * A source column in the rows, or a grouping of a date column by month or day. A grouping is a
 * field of its own in Excel, named like Excel names it ("Monate (Start)"). A collapsed field
 * shows its items with their totals, and the reader opens one to see the fields below it.
 */
export type PivotRowField =
  | { column: number; collapsed?: boolean }
  | { column: number; groupBy: PivotDateGroup; name: string; collapsed?: boolean };

export type PivotDateGroup = "months" | "days";

/**
 * Placeholder month names for the group items. Excel needs the items in the file but names them
 * afresh in the reader's language when it reads the data on opening (checked in a German Excel
 * 16: these English names came up as "Okt", "01. Okt"), so their language does not matter.
 */
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A column summarised in the values area. */
export interface PivotDataField {
  column: number;
  /** As shown in the table, e.g. "Summe von Strecke (km)". */
  name: string;
  /** The number format, as a style id from the stylesheet. */
  numFmtId: number;
  /** How the values are summarised; a sum when left out. */
  subtotal?: "sum" | "min" | "max" | "average" | "count";
}

/** Finest first, which is the order Excel keeps the groups of one date field in. */
const GROUP_ORDER: PivotDateGroup[] = ["days", "months"];

interface DateGroupField {
  base: number;
  groupBy: PivotDateGroup;
  name: string;
  /** The names between the before and after items, see {@link groupItemNames}. */
  labels: string[];
  /** Its index among the cache fields, after the source columns. */
  index: number;
}

/** The items of a source column: its distinct values, in the order Excel lists them. */
interface ColumnItems {
  /** `<sharedItems>` with its attributes and children. */
  sharedItemsXml: string;
  /** Indexes into the shared items, sorted as the pivot table shows them. */
  displayOrder: number[];
  /** Earliest and latest date, for a date column. */
  dateRange?: { min: Date; max: Date };
  /** Dates only, none missing: what Excel can group by month or day. */
  groupable: boolean;
}

/**
 * Where the pivot table reads from, and its fields.
 *
 * No records are written (`saveData="0"`) and Excel reads the rows itself on opening
 * (`refreshOnLoad`): a second copy of a year of trips would double the file for nothing.
 */
export function getPivotCacheDefinitionXml(pivot: PivotTableSpec, sourceSheetName: string) {
  // A range of only the header row is not a valid source; one empty row below it is.
  const lastRow = Math.max(pivot.lastRow, pivot.headerRow + 1);
  const ref = `A${pivot.headerRow}:${columnNumberToName(pivot.fieldNames.length)}${lastRow}`;
  const model = buildModel(pivot);

  const sourceFields = uniquePivotFieldNames(pivot.fieldNames).map((name, column) => {
    const items = model.itemsByColumn.get(column);
    const groups = model.groups.filter(group => group.base === column);
    // A date field points at the coarsest of its groups, as Excel writes it.
    const fieldGroup = groups.length > 0 ? `<fieldGroup par="${groups[groups.length - 1].index}"/>` : "";
    const numFmtId = items?.dateRange ? ' numFmtId="22"' : ' numFmtId="0"';
    return `<cacheField name="${escapeXml(name)}"${numFmtId}>${items?.sharedItemsXml ?? "<sharedItems/>"}${fieldGroup}</cacheField>`;
  });

  const groupFields = model.groups.map(group => {
    const range = model.itemsByColumn.get(group.base)?.dateRange;
    const start = range ? localDateTime(range.min) : "";
    const end = range ? localDateTime(range.max) : "";
    const items = groupItemNames(group, range).map(label => `<s v="${escapeXml(label)}"/>`).join("");
    return [
      `<cacheField name="${escapeXml(group.name)}" numFmtId="0" databaseField="0">`,
      `<fieldGroup base="${group.base}">`,
      `<rangePr groupBy="${group.groupBy}" startDate="${start}" endDate="${end}"/>`,
      `<groupItems count="${group.labels.length + 2}">${items}</groupItems>`,
      "</fieldGroup></cacheField>",
    ].join("");
  });

  const fieldCount = sourceFields.length + groupFields.length;
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<pivotCacheDefinition xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ',
    'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ',
    'saveData="0" refreshOnLoad="1" createdVersion="6" refreshedVersion="6" minRefreshableVersion="3" recordCount="0">',
    `<cacheSource type="worksheet"><worksheetSource ref="${ref}" sheet="${escapeXml(sourceSheetName)}"/></cacheSource>`,
    `<cacheFields count="${fieldCount}">${sourceFields.join("")}${groupFields.join("")}</cacheFields>`,
    "</pivotCacheDefinition>",
  ].join("");
}

/** The pivot table itself: every field offered, the layout's fields placed. */
export function getPivotTableXml(pivot: PivotTableSpec, pivotNumber: number) {
  const model = buildModel(pivot);
  const fieldCount = pivot.fieldNames.length + model.groups.length;
  const dataColumns = new Set(model.dataFields.map(field => field.column));

  const tabular = pivot.layout?.tabular === true;
  // In the tabular form every field says so itself; Excel's own default is the compact form.
  const fieldForm = tabular ? ' compact="0" outline="0"' : "";

  const pivotFields = Array.from({ length: fieldCount }, (_unused, index) => {
    const onRows = model.rowFieldIndexes.includes(index);
    const dataField = dataColumns.has(index) ? ' dataField="1"' : "";
    if (!onRows) return `<pivotField${dataField}${fieldForm} showAll="0"/>`;

    const group = model.groups.find(candidate => candidate.index === index);
    const order = group
      ? Array.from({ length: group.labels.length + 2 }, (_v, item) => item)
      : (model.itemsByColumn.get(index)?.displayOrder ?? []);
    const collapsed = model.collapsedIndexes.has(index) ? ' sd="0"' : "";
    const items = order.map(item => `<item${collapsed} x="${item}"/>`).join("");
    return `<pivotField axis="axisRow"${dataField}${fieldForm} showAll="0"><items count="${order.length + 1}">${items}<item t="default"/></items></pivotField>`;
  });

  const rowFields =
    model.rowFieldIndexes.length === 0
      ? ""
      : `<rowFields count="${model.rowFieldIndexes.length}">${model.rowFieldIndexes.map(index => `<field x="${index}"/>`).join("")}</rowFields>`;
  // Several values sit side by side; -2 is Excel's stand-in for "the values" as a column field.
  const colFields = model.dataFields.length > 1 ? '<colFields count="1"><field x="-2"/></colFields>' : "";
  const dataFields =
    model.dataFields.length === 0
      ? ""
      : `<dataFields count="${model.dataFields.length}">${model.dataFields
          .map(field => {
            const subtotal = field.subtotal && field.subtotal !== "sum" ? ` subtotal="${field.subtotal}"` : "";
            return `<dataField name="${escapeXml(field.name)}" fld="${field.column}"${subtotal} baseField="0" baseItem="0" numFmtId="${field.numFmtId}"/>`;
          })
          .join("")}</dataFields>`;
  const hasLayout = rowFields !== "" || dataFields !== "";
  const form = tabular ? 'compact="0" compactData="0"' : 'outline="1" outlineData="1"';
  // Excel lays the rows out itself on opening; it only needs a corner to start from. Row 3 is
  // where it puts a new pivot table on a sheet of its own.
  const top = pivot.locationRow ?? 3;
  const end = `${columnNumberToName(Math.max(2, 1 + model.dataFields.length))}${top + 1}`;
  const location = hasLayout
    ? `<location ref="A${top}:${end}" firstHeaderRow="${tabular ? 0 : 1}" firstDataRow="1" firstDataCol="${dataFields ? 1 : 0}"/>`
    : `<location ref="A${top}:C${top + 17}" firstHeaderRow="1" firstDataRow="1" firstDataCol="0"/>`;

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<pivotTableDefinition xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ',
    `name="PivotTable${pivotNumber}" cacheId="${pivotNumber}" applyNumberFormats="0" applyBorderFormats="0" `,
    'applyFontFormats="0" applyPatternFormats="0" applyAlignmentFormats="0" applyWidthHeightFormats="1" ',
    'dataCaption="Values" updatedVersion="6" minRefreshableVersion="3" useAutoFormatting="1" ',
    `itemPrintTitles="1" createdVersion="6" indent="0" ${form} multipleFieldFilters="0">`,
    location,
    `<pivotFields count="${fieldCount}">${pivotFields.join("")}</pivotFields>`,
    rowFields,
    colFields,
    dataFields,
    '<pivotTableStyleInfo name="PivotStyleLight16" showRowHeaders="1" showColHeaders="1" showRowStripes="0" showColStripes="0" showLastColumn="1"/>',
    // Side by side, Excel would put a row reading "Values" above their names; the owner's template
    // hides it, as Excel 2010 and later can.
    colFields
      ? '<extLst><ext uri="{962EF5D1-5CA2-4c93-8EF4-DBF5C05439D2}" xmlns:x14="http://schemas.microsoft.com/office/spreadsheetml/2009/9/main"><x14:pivotTableDefinition hideValuesRow="1"/></ext></extLst>'
      : "",
    "</pivotTableDefinition>",
  ].join("");
}

/** The layout resolved against the columns: which fields exist, where they sit, their items. */
function buildModel(pivot: PivotTableSpec) {
  const columnCount = pivot.fieldNames.length;
  const inRange = (column: number) => Number.isInteger(column) && column >= 0 && column < columnCount;
  const placedRows = (pivot.layout?.rowFields ?? []).filter(field => inRange(field.column));
  const dataFields = (pivot.layout?.dataFields ?? []).filter(field => inRange(field.column));

  // Items are needed for every placed column, and for the base of every date grouping.
  const itemsByColumn = new Map<number, ColumnItems>();
  for (const { column } of placedRows) {
    if (!itemsByColumn.has(column)) {
      itemsByColumn.set(column, columnItems((pivot.rows ?? []).map(row => row[column])));
    }
  }

  // Excel groups only a column of dates without gaps (its own grouping refuses anything else, and
  // a file claiming one does not open), so such a grouping is left out rather than the table.
  const layoutRows = placedRows.filter(
    field => !("groupBy" in field) || itemsByColumn.get(field.column)?.groupable,
  );

  const groups: DateGroupField[] = [];
  for (const groupBy of GROUP_ORDER) {
    for (const field of layoutRows) {
      if (!("groupBy" in field) || field.groupBy !== groupBy) continue;
      if (groups.some(group => group.base === field.column && group.groupBy === groupBy)) continue;
      groups.push({
        base: field.column,
        groupBy,
        name: field.name,
        labels: dateGroupLabels(groupBy),
        index: 0,
      });
    }
  }
  // Grouped by base so a date field's groups sit next to each other, finest first.
  groups.sort((a, b) => a.base - b.base || GROUP_ORDER.indexOf(a.groupBy) - GROUP_ORDER.indexOf(b.groupBy));
  groups.forEach((group, position) => (group.index = columnCount + position));

  const rowFieldIndexes = layoutRows.map(field =>
    "groupBy" in field
      ? groups.find(group => group.base === field.column && group.groupBy === field.groupBy)!.index
      : field.column,
  );
  const collapsedIndexes = new Set(
    rowFieldIndexes.filter((_index, position) => layoutRows[position].collapsed),
  );

  return { groups, rowFieldIndexes, collapsedIndexes, dataFields, itemsByColumn };
}

/**
 * The distinct values of a column, as Excel's shared items.
 *
 * Text is told apart without regard to case, as Excel does; two items it would see as one make
 * it reject the cache. An empty cell is the missing item `<m/>`.
 */
function columnItems(values: WorkbookCell[]): ColumnItems {
  const keys = new Map<string, number>();
  const items: { xml: string; sortKey: string | number; isBlank: boolean }[] = [];
  let hasText = false;
  let hasNumber = false;
  let hasDate = false;
  let hasBlank = false;
  let minDate: Date | undefined;
  let maxDate: Date | undefined;
  let minNumber = Number.POSITIVE_INFINITY;
  let maxNumber = Number.NEGATIVE_INFINITY;

  const add = (key: string, xml: string, sortKey: string | number, isBlank = false) => {
    if (keys.has(key)) return;
    keys.set(key, items.length);
    items.push({ xml, sortKey, isBlank });
  };

  for (const cell of values) {
    // A formula counts with the result written beside it, which is what Excel reads too.
    const value = isFormula(cell) ? cell.value : cell;
    if (value === null || value === undefined || value === "" || (value instanceof Date && Number.isNaN(value.getTime()))) {
      hasBlank = true;
      add("m", "<m/>", "", true);
    } else if (value instanceof Date) {
      hasDate = true;
      // Excel keeps a pivot date to the second, rounded: 52.912 s is 53 s. Two trips a few
      // milliseconds apart are one item to it, and a cache listing them twice is refused.
      const time = new Date(Math.round(value.getTime() / 1000) * 1000);
      if (!minDate || time < minDate) minDate = time;
      if (!maxDate || time > maxDate) maxDate = time;
      add(`d${time.getTime()}`, `<d v="${localDateTime(time)}"/>`, time.getTime());
    } else if (typeof value === "number" && Number.isFinite(value)) {
      hasNumber = true;
      minNumber = Math.min(minNumber, value);
      maxNumber = Math.max(maxNumber, value);
      add(`n${value}`, `<n v="${value}"/>`, value);
    } else {
      hasText = true;
      const text = String(value);
      add(`s${text.toLowerCase()}`, `<s v="${escapeXml(text)}"/>`, text);
    }
  }

  // "Semi-mixed" means text or blanks besides other values, so it is claimed absent only when
  // there are neither; Excel refuses a cache that says otherwise.
  const attributes: string[] = [];
  if (hasDate && !hasText && !hasNumber) {
    if (!hasBlank) attributes.push('containsSemiMixedTypes="0"');
    attributes.push('containsNonDate="0"', 'containsDate="1"', 'containsString="0"');
    if (hasBlank) attributes.push('containsBlank="1"');
    attributes.push(`minDate="${localDateTime(minDate!)}"`, `maxDate="${localDateTime(maxDate!)}"`);
  } else if (hasNumber && !hasText && !hasDate) {
    if (!hasBlank) attributes.push('containsSemiMixedTypes="0"');
    attributes.push('containsString="0"', 'containsNumber="1"');
    if (hasBlank) attributes.push('containsBlank="1"');
    attributes.push(`minValue="${minNumber}"`, `maxValue="${maxNumber}"`);
  } else if (hasBlank) {
    attributes.push('containsBlank="1"');
  }
  attributes.push(`count="${items.length}"`);

  // Shown sorted, with an empty value last, as Excel orders a new field.
  const displayOrder = items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      if (a.item.isBlank !== b.item.isBlank) return a.item.isBlank ? 1 : -1;
      const [x, y] = [a.item.sortKey, b.item.sortKey];
      return typeof x === "number" && typeof y === "number"
        ? x - y
        : String(x).localeCompare(String(y), undefined, { sensitivity: "base" });
    })
    .map(({ index }) => index);

  return {
    sharedItemsXml: `<sharedItems ${attributes.join(" ")}>${items.map(item => item.xml).join("")}</sharedItems>`,
    displayOrder,
    ...(minDate && maxDate && hasDate && !hasText && !hasNumber ? { dateRange: { min: minDate, max: maxDate } } : {}),
    groupable: hasDate && !hasText && !hasNumber && !hasBlank,
  };
}

/**
 * A group's items: one for the values before the range, the labels, one for those after it —
 * the shape Excel writes. Excel renames them in its own language when it refreshes.
 */
function groupItemNames(group: DateGroupField, range?: { min: Date; max: Date }) {
  const before = range ? `<${shortDate(range.min)}` : "<";
  const after = range ? `>${shortDate(range.max)}` : ">";
  return [before, ...group.labels, after];
}

/** The groups of a date grouping: twelve months, or the 366 days of a leap year as "01. Jan". */
function dateGroupLabels(groupBy: PivotDateGroup): string[] {
  if (groupBy === "months") return MONTH_NAMES;
  const labels: string[] = [];
  MONTH_NAMES.forEach((month, index) => {
    // 2000 is a leap year, so February has its 29th.
    const days = new Date(2000, index + 1, 0).getDate();
    for (let day = 1; day <= days; day++) {
      labels.push(`${String(day).padStart(2, "0")}. ${month}`);
    }
  });
  return labels;
}

/** The wall-clock time a cell shows, as Excel writes dates into a pivot cache. */
function localDateTime(date: Date) {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function shortDate(date: Date) {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/**
 * Excel refuses a pivot cache whose field names repeat (case-insensitive) or are empty, so an
 * empty one becomes `Column<n>` and later repeats get " 2", " 3", …
 */
export function uniquePivotFieldNames(names: string[]) {
  const used = new Set<string>();
  return names.map((raw, index) => {
    const base = raw.trim() || `Column${index + 1}`;
    let name = base;
    for (let n = 2; used.has(name.toLowerCase()); n++) {
      name = `${base} ${n}`;
    }
    used.add(name.toLowerCase());
    return name;
  });
}
